/*
 * PeterCox first-party event tracker.
 *
 *   Posts events to petercox.ie/apps/events/collect — a Shopify App Proxy
 *   route forwarded to the site-events-collector service (project: cairn),
 *   HMAC-signed by Shopify's edge so the backend can trust the call.
 *
 *   Lives outside of theme.js per shopify/CLAUDE.md "Do NOT modify theme.js
 *   for new functionality". Bootstrapped from snippets/custom-event-tracker.liquid
 *   which sets window.PeterCox.product / .collection / .currency before this
 *   script loads with `defer`.
 *
 *   Adblock-immune because the request is same-origin (petercox.ie), not a
 *   third-party tracker domain. AC7 of cairn/spec-site-events-collector.
 */
(function () {
  'use strict';

  var COLLECT_URL = '/apps/events/collect';
  var COOKIE_DOMAIN = '.petercox.ie';
  var VISITOR_COOKIE = 'pcx_v';
  var SESSION_COOKIE = 'pcx_s';
  var VISITOR_MAX_AGE = 60 * 60 * 24 * 365 * 2; // 2 years
  var SESSION_MAX_AGE = 60 * 30;                // 30 minutes
  var ENGAGED_THRESHOLD_MS = 30 * 1000;
  var SCROLL_MILESTONES_PCT = [25, 50, 75, 100];

  window.PeterCox = window.PeterCox || {};

  // ─── UUID + cookies ─────────────────────────────────────────────────────────
  function generateUUID() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function readCookie(name) {
    var prefix = name + '=';
    var parts = document.cookie ? document.cookie.split('; ') : [];
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].indexOf(prefix) === 0) {
        return parts[i].slice(prefix.length);
      }
    }
    return null;
  }

  function writeCookie(name, value, maxAge) {
    var parts = [
      name + '=' + value,
      'Domain=' + COOKIE_DOMAIN,
      'Path=/',
      'Max-Age=' + maxAge,
      'SameSite=Lax'
    ];
    if (location.protocol === 'https:') parts.push('Secure');
    document.cookie = parts.join('; ');
  }

  function ensureVisitorId() {
    var existing = readCookie(VISITOR_COOKIE);
    if (existing) return existing;
    var fresh = generateUUID();
    writeCookie(VISITOR_COOKIE, fresh, VISITOR_MAX_AGE);
    return fresh;
  }

  function ensureSessionId() {
    var existing = readCookie(SESSION_COOKIE);
    if (existing) {
      writeCookie(SESSION_COOKIE, existing, SESSION_MAX_AGE);
      return existing;
    }
    var fresh = generateUUID();
    writeCookie(SESSION_COOKIE, fresh, SESSION_MAX_AGE);
    return fresh;
  }

  // ─── URL attribution + cross-system identity ────────────────────────────────
  //
  // parseAttribution / getKlaviyoIdCookie / getCurrency / getShopifyVisitorCookie
  // USED TO LIVE HERE. They moved to the inline entry block in
  // snippets/custom-event-tracker.liquid (2026-08-16, spec
  // cairn/spec-early-identity-and-entry-pageview) when the collector envelope
  // gained a single builder, window.PeterCox.buildEnvelope. They are not
  // duplicated here on purpose: two builders drifting apart was the top
  // watch-point of that spec. emit() below consumes the one builder and throws
  // loudly if it is missing.

  // NO CART TOKEN HERE, DELIBERATELY (removed 2026-08-15).
  //
  // A cached Shopify cart token used to live at this point and ride on every
  // event as the order↔session join key. It was removed because Shopify
  // documents the cart token as a random identifier that WILL change: an empty
  // cart returns a different token on every /cart.js request, so the cached
  // value named a cart that never became the order. Measured 2026-08-13: 46 of
  // 47 token-carrying sessions held an unusable value. The matching order-side
  // field is also deprecation-marked on both REST and GraphQL.
  //
  // (This note deliberately never spells the identifier — the guard below is a
  // strict string-absence sweep over committed theme source, and a tombstone
  // that named it would trip its own guard.)
  //
  // The surviving theme-side join leg is the pcx_v/pcx_s CART ATTRIBUTES
  // (see ensureCartAttributes below), which flow into order.note_attributes at
  // checkout completion and are ours, not Shopify's. Do not reintroduce a
  // token leg here — tests/pcx-cart-token-removed.test.mjs guards it.

  // ─── Event emission ─────────────────────────────────────────────────────────
  //
  // ONE envelope builder, defined by the inline entry block in
  // snippets/custom-event-tracker.liquid, which runs synchronously in <head>
  // before this deferred script exists. If it is missing, this THROWS rather
  // than hand-rolling a second envelope: a silent fallback is exactly the drift
  // the single-builder rule exists to prevent, and track() below turns the
  // throw into a console warning rather than a page error.
  function emit(eventType, properties) {
    var build = window.PeterCox && window.PeterCox.buildEnvelope;
    if (typeof build !== 'function') {
      throw new Error(
        'pcx-tracker: window.PeterCox.buildEnvelope is missing — the inline entry ' +
        'block in snippets/custom-event-tracker.liquid did not run'
      );
    }
    var payload = build(eventType, properties);
    return fetch(COLLECT_URL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    });
  }

  function track(eventType, properties) {
    if (!eventType || typeof eventType !== 'string') return;
    try {
      var p = emit(eventType, properties);
      if (p && typeof p.catch === 'function') {
        p.catch(function (e) {
          if (window.console && console.warn) console.warn('pcx-tracker network error', e);
        });
      }
      return p;
    } catch (e) {
      if (window.console && console.warn) console.warn('pcx-tracker error', e);
    }
  }

  // ─── Event fire helpers ─────────────────────────────────────────────────────
  function firePageViewed() {
    var props = {};
    var product = window.PeterCox.product;
    var collection = window.PeterCox.collection;
    if (product && product.id) props.product_id = product.id;
    if (collection && collection.handle) props.collection_handle = collection.handle;
    track('page_viewed', props);
  }

  function fireProductViewed() {
    var p = window.PeterCox.product;
    if (!p || !p.id) return;
    track('product_viewed', {
      product_id: p.id,
      product_gid: p.gid || null,
      product_title: p.title || null,
      variant_id: p.variant_id || null
    });
  }

  function fireCollectionViewed() {
    var c = window.PeterCox.collection;
    if (!c || !c.handle) return;
    track('collection_viewed', {
      collection_handle: c.handle,
      collection_title: c.title || null,
      collection_id: c.id || null
    });
  }

  // Read the on-page search result count so zero-result searches are
  // measurable (F, spec replay-v2). Prefer the server-rendered count exposed
  // on window.PeterCox.search.resultCount (set by the bootstrap snippet from
  // Liquid `search.results_count`); fall back to counting rendered result
  // nodes. Returns null when neither is available (the property is then
  // omitted — the server numeric-validates result_count only when present).
  function getSearchResultCount() {
    var s = window.PeterCox.search;
    if (s && typeof s.resultCount === 'number' && s.resultCount >= 0) {
      return s.resultCount;
    }
    try {
      var nodes = document.querySelectorAll('[data-search-result], .search-result, li.grid__item');
      if (nodes && nodes.length >= 0) return nodes.length;
    } catch (e) { /* querySelectorAll unsupported — give up */ }
    return null;
  }

  function fireSearchPerformedIfSearchPage() {
    if (location.pathname !== '/search') return;
    try {
      var q = new URLSearchParams(location.search).get('q');
      if (!q) return;
      var props = { query: q.slice(0, 256) };
      var rc = getSearchResultCount();
      if (rc !== null) props.result_count = rc;
      track('search_performed', props);
    } catch (e) { /* skip on URLSearchParams failure */ }
  }

  // ─── Cart-related events ────────────────────────────────────────────────────
  // The theme has TWO product-form mechanisms — legacy Impulse (assets/theme.js
  // ProductForm.success → dispatches ajaxProduct:added) and the modern
  // <product-form> custom element (snippets/form.product.liquid → dispatches
  // product-form:submit:success). The modern element is only rendered by
  // section.flex-pdp (main-product-high-variant template); every other PDP
  // template uses the legacy path. Both are wired here so neither silently
  // misses if the high-variant template is enabled on any product.
  //
  // Dynamic checkout buttons (Shop Pay / Apple Pay / Google Pay rendered by
  // {{ form | payment_button }}) bypass BOTH form paths — they submit the
  // form directly to Shopify checkout without firing /cart/add.js. The
  // payment-button click handler below catches them. Without it, every
  // Shop Pay tap registers as a checkout with no preceding add_to_cart.

  // Shared writer: takes a /cart/add.js-shaped line item and emits the event.
  // Both onAjaxProductAdded and onProductFormAdded delegate here.
  function emitAddToCart(line) {
    if (!line) return;
    track('add_to_cart', {
      variant_id: line.id || line.variant_id || null,
      product_id: line.product_id || null,
      quantity: line.quantity || 1,
      product_title: line.title || line.product_title || null,
      price_cents: typeof line.price === 'number' ? line.price : null
    });
  }

  function onAjaxProductAdded(evt) {
    var detail = evt && evt.detail;
    var product = detail && detail.product;
    // detail.product is the /cart/add.js response (Impulse ProductForm.success
    // at theme.js:2851 + main-product-high-variant.liquid forwarder). When
    // it's absent (theme.js:2824 dispatches no-detail on a 422 failed add),
    // skip rather than write a blank row — see cairn cycle 1 AUD-PCX-003.
    if (!product) return;
    emitAddToCart(product);
  }

  // Modern <product-form> custom element (snippets/form.product.liquid line
  // 171): detail = { form, response, cart }. `response` is the parsed
  // /cart/add.js response — same shape as legacy detail.product.
  function onProductFormAdded(evt) {
    var detail = evt && evt.detail;
    if (!detail || !detail.response) return;
    emitAddToCart(detail.response);
  }

  // Dynamic checkout buttons (Shop Pay / Apple Pay / Google Pay). Capture
  // phase so we fire BEFORE Shopify's own click handler triggers the
  // navigation. The PDP context (current product + selected variant) comes
  // from window.PeterCox.product.
  //
  // SELECTOR — only .shopify-payment-button: the theme wrapper
  // .payment-buttons (snippets/product-form.liquid:22) also contains the
  // legacy button[name="add"] when enable_dynamic_buttons is on, so matching
  // it double-fires add_to_cart on every regular ATC click. Restrict to the
  // Shopify-rendered container so only real payment-button clicks match.
  //
  // VARIANT_ID — falls back to window.PeterCox.product.variant_id when the
  // form has no input[name="id"]. The product/mug PDP forms here only carry
  // input[name="product-id"]; the variant input is injected at submit time by
  // the variant picker. PeterCox.product.variant_id is set server-side to
  // product.selected_or_first_available_variant.id and is always populated.
  //
  // PRICE-DATA — bootstrap exposes {id, gid, title, variant_id} with no
  // per-variant prices, so price_cents is null here. variant_id is the
  // durable join key; cairn attributes revenue from the conversion event
  // that follows in checkout. Extend the bootstrap with current_price_cents
  // refreshed on variant:change if per-variant price ever becomes needed.
  function onPaymentButtonClick(evt) {
    var target = evt && evt.target;
    if (!target || typeof target.closest !== 'function') return;
    var wrapper = target.closest('.shopify-payment-button');
    if (!wrapper) return;
    var form = wrapper.closest('form');
    var product = window.PeterCox && window.PeterCox.product;
    if (!form || !product) return;
    var variantInput = form.querySelector('input[name="id"]');
    var qtyInput = form.querySelector('input[name="quantity"]');
    var variantId = (variantInput && Number(variantInput.value)) || product.variant_id || null;
    emitAddToCart({
      id: variantId,
      product_id: product.id || null,
      quantity: qtyInput ? Number(qtyInput.value) || 1 : 1,
      title: product.title || null,
      price: null
    });
    // Shop Pay / Apple Pay / Google Pay go STRAIGHT to checkout from the PDP —
    // they never touch the cart page, so onCheckoutClick can never see them.
    // Without this, express checkouts would be silently missing from the
    // funnel and every express buyer would look like an add-to-cart that
    // vanished. Same event, different trigger, so the two are separable.
    fireCheckoutStarted('payment_button', {
      product_id: product.id || null,
      variant_id: variantId
    });
  }

  // ─── Checkout start ─────────────────────────────────────────────────────────
  // FIRST-PARTY checkout instrumentation. The Checkout button lives on OUR
  // pages (sections/main-cart.liquid and snippets/cart-drawer.liquid, both
  // `button[name="checkout"]`), so we do not need Shopify's Web Pixel, its
  // sandbox, or any cookie surviving the origin hop to know a visitor entered
  // checkout. This fires on our own domain, through the App Proxy, inside the
  // real browsing session — which is exactly what every third-party attempt at
  // this signal has failed to deliver.
  //
  // Why this exists (2026-08-12): the Web Pixel's checkout_started has produced
  // 2 rows ever, both from staff tests, and Shopify's own session analytics was
  // measured undercounting completed checkouts by ~26% against real orders. A
  // click we can see ourselves is the only leg not dependent on someone else's
  // counting.
  //
  // NOT identical to Shopify's "reached checkout": this is the click that
  // starts the navigation. A click that fails to navigate still counts, and a
  // double-click could count twice — see the dedupe below. Treat it as
  // "intent to check out", and validate it against Shopify's own figure rather
  // than assuming the two must match.
  //
  // Capture phase, so we fire BEFORE the form submit navigates away, and
  // `emit()` uses keepalive:true so the POST survives the unload.
  var _checkoutStartedAt = 0;

  function fireCheckoutStarted(trigger, extra) {
    // Dedupe: a double-click, or the drawer button and the cart form both
    // matching, would otherwise write two starts for one checkout. 3s is long
    // enough to swallow an impatient double-tap and far shorter than any real
    // return-to-cart-and-retry, which SHOULD count again.
    var now = Date.now();
    if (now - _checkoutStartedAt < 3000) return;
    _checkoutStartedAt = now;
    var props = { trigger: trigger };
    if (extra) {
      Object.keys(extra).forEach(function (k) {
        if (extra[k] !== null && extra[k] !== undefined) props[k] = extra[k];
      });
    }
    track('checkout_started', props);
  }

  function onCheckoutClick(evt) {
    var target = evt && evt.target;
    if (!target || typeof target.closest !== 'function') return;
    // name="checkout" is the Shopify convention for the cart form's submit
    // button and is what both our cart surfaces use.
    var btn = target.closest('button[name="checkout"], input[name="checkout"]');
    if (!btn) return;
    // A disabled button (terms checkbox not ticked) does not navigate, so it
    // is not a checkout start.
    if (btn.disabled) return;
    var cart = _lastCartItems;
    fireCheckoutStarted('cart_button', {
      num_items: Array.isArray(cart) ? cart.length : null,
      quantity: Array.isArray(cart)
        ? cart.reduce(function (n, l) { return n + (l.quantity || 0); }, 0)
        : null
    });
  }

  // Variant selection on PDP — Impulse dispatches variant:change on document
  // with the chosen variant object as event.detail (or detail.variant on some
  // sections). Captures the format/size switching that precedes ATC.
  function onVariantChange(evt) {
    var variant = evt && evt.detail && (evt.detail.variant || evt.detail);
    if (!variant || !variant.id) return;
    _lastVariantId = variant.id;
    var product = window.PeterCox.product;
    track('variant_changed', {
      product_id: product ? product.id : null,
      variant_id: variant.id,
      variant_title: variant.title || null,
      price_cents: typeof variant.price === 'number' ? variant.price : null,
      available: typeof variant.available === 'boolean' ? variant.available : null
    });
  }

  // Cart-attribute write-through — embed pcx_v + pcx_s into cart.attributes
  // so they flow into order.note_attributes (REST) / order.customAttributes
  // (GraphQL) at checkout completion. This is the primary, deterministic
  // session-to-order join key for the cairn conversion-tracking pipeline —
  // works independent of the Web Pixel firing. Verified end-to-end against
  // order WO-4655 on 2026-05-22 (cairn/plan-conversion-tracking Step 1).
  //
  // Cart attributes merge on POST /cart/update.js: keys present in the body
  // upsert; keys not present are preserved (Shopify Cart AJAX semantics).
  function ensureCartAttributes(cart) {
    if (!cart) return;
    var attrs = cart.attributes || {};
    var vid = ensureVisitorId();
    var sid = ensureSessionId();
    if (attrs.pcx_v === vid && attrs.pcx_s === sid) return;
    fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { pcx_v: vid, pcx_s: sid } }),
      keepalive: true
    }).catch(function (e) {
      if (window.console && console.warn) console.warn('pcx-tracker cart-attrs write failed', e);
    });
  }

  // Backfill cart attributes for returning visitors with a pre-tracker cart.
  //
  // THIS FETCH IS LOAD-BEARING ON ITS OWN. It once had a second justification
  // ("token first, unconditionally") that died with the cart token; its
  // remaining job is the one named above and is the whole reason it exists — a
  // visitor who built a cart before this tracker ran, or before the attributes
  // were written, is otherwise never tagged and their order never joins back to
  // a session. Skips empty carts (no order will reference them; saves a
  // network call). Pinned by tests/pcx-cart-attributes.test.mjs row 10.
  function fireCartAttributesInit() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart && Array.isArray(cart.items) && cart.items.length > 0) {
          ensureCartAttributes(cart);
        }
      })
      .catch(function (e) {
        if (window.console && console.warn) console.warn('pcx-tracker init cart fetch failed', e);
      });
  }

  // Track cart removals by diffing successive cart:updated snapshots.
  // Impulse fires cart:updated with detail.cart (the /cart.js shape).
  var _lastCartItems = null;
  function onCartUpdated(evt) {
    var cart = evt && evt.detail && evt.detail.cart;
    if (!cart || !Array.isArray(cart.items)) return;
    ensureCartAttributes(cart);
    var prev = _lastCartItems;
    _lastCartItems = cart.items.map(function (i) {
      return { variant_id: i.variant_id || i.id, quantity: i.quantity, title: i.title, product_id: i.product_id };
    });
    if (!prev) return; // first observation; no diff possible
    // For each previous line, find current quantity. A drop in quantity (or
    // disappearance) → emit remove_from_cart with the delta.
    prev.forEach(function (oldLine) {
      var nowLine = _lastCartItems.find(function (l) { return l.variant_id === oldLine.variant_id; });
      var oldQty = oldLine.quantity || 0;
      var newQty = nowLine ? (nowLine.quantity || 0) : 0;
      if (newQty < oldQty) {
        track('remove_from_cart', {
          variant_id: oldLine.variant_id,
          product_id: oldLine.product_id || null,
          product_title: oldLine.title || null,
          quantity_removed: oldQty - newQty,
          quantity_remaining: newQty
        });
      }
    });
  }

  // ─── Engagement events ──────────────────────────────────────────────────────
  var _engagedFired = false;
  var _engagedElapsed = 0;
  var _engagedLastResume = null;

  function startEngagementTimer() {
    if (document.visibilityState !== 'visible') return;
    _engagedLastResume = Date.now();
    setTimeout(checkEngaged, ENGAGED_THRESHOLD_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'visible') {
      _engagedLastResume = Date.now();
      if (!_engagedFired) setTimeout(checkEngaged, Math.max(0, ENGAGED_THRESHOLD_MS - _engagedElapsed));
    } else if (_engagedLastResume) {
      _engagedElapsed += Date.now() - _engagedLastResume;
      _engagedLastResume = null;
    }
  }

  function checkEngaged() {
    if (_engagedFired) return;
    var elapsed = _engagedElapsed + (_engagedLastResume ? (Date.now() - _engagedLastResume) : 0);
    if (elapsed >= ENGAGED_THRESHOLD_MS && document.visibilityState === 'visible') {
      _engagedFired = true;
      track('page_engaged', { dwell_ms: Math.round(elapsed) });
    }
  }

  // Scroll depth milestones — fire each at most once per page.
  var _scrollFired = {};
  function startScrollDepthListener() {
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        var doc = document.documentElement;
        var scrollTop = window.scrollY || doc.scrollTop || 0;
        var winHeight = window.innerHeight || doc.clientHeight || 0;
        var docHeight = Math.max(
          document.body.scrollHeight, doc.scrollHeight,
          document.body.offsetHeight, doc.offsetHeight,
          doc.clientHeight
        );
        var scrollable = Math.max(1, docHeight - winHeight);
        var pct = Math.min(100, Math.round(((scrollTop + winHeight) / docHeight) * 100));
        SCROLL_MILESTONES_PCT.forEach(function (milestone) {
          if (pct >= milestone && !_scrollFired[milestone]) {
            _scrollFired[milestone] = true;
            track('scroll_depth', { depth_pct: milestone });
          }
        });
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // ─── Visual interactions (PhotoSwipe zoom, Flickity gallery, model-viewer) ──
  // Impulse uses PhotoSwipe for image zoom (triggered by clicking the product
  // image / zoom button). PhotoSwipe dispatches an 'open' / 'opened' event on
  // its instance. Easier hook: click on the trigger element. We catch both
  // by listening for clicks within .product__photos AND for a globally
  // dispatched 'pswp:opened' if the theme fires one.
  function startImageZoomListener() {
    document.addEventListener('click', function (evt) {
      var target = evt.target;
      if (!target) return;
      // Photoswipe trigger: clicks on product photo zoom buttons typically
      // carry data-photoswipe or are .pswp__... ancestors.
      var trigger = target.closest && target.closest('[data-photoswipe], .product-image-main, .product__photo-zoom, .pswp__button--zoom');
      if (!trigger) return;
      var product = window.PeterCox.product;
      track('image_zoomed', {
        product_id: product ? product.id : null,
        trigger: trigger.tagName.toLowerCase() + (trigger.className ? '.' + String(trigger.className).split(/\s+/)[0] : '')
      });
    });
  }

  // Impulse uses Flickity for the product image carousel. Flickity's events
  // (`select`, `change`) are emitted on the Flickity *instance*, not as DOM
  // events on the container — `document.addEventListener('change.flickity', …)`
  // does NOT fire (jQuery-namespaced events stay inside jQuery's dispatch
  // table). Cycle-3 audit DEEP-001 caught this as dead code.
  //
  // Fix: delegate clicks on Flickity's navigation UI. Covers manual
  // next/prev clicks and dot clicks. Drag-to-swipe is intentionally not
  // counted as a gallery navigation (a single drag often crosses 2+ cells
  // unintentionally — would inflate the metric).
  function startGalleryListener() {
    document.addEventListener('click', function (evt) {
      var target = evt.target;
      if (!target || !target.closest) return;
      var btn = target.closest(
        '.flickity-prev-next-button, .flickity-page-dots .dot'
      );
      if (!btn) return;
      var carousel = btn.closest('.flickity-enabled, [data-flickity]') || null;
      var product = window.PeterCox.product;
      track('gallery_navigated', {
        product_id: product ? product.id : null,
        control: btn.classList && btn.classList.contains('flickity-prev-next-button')
          ? 'arrow'
          : 'dot',
        carousel_id: carousel ? (carousel.id || null) : null
      });
    });
  }

  // model-viewer dispatches 'ar-status' + 'camera-change' as non-bubbling
  // CustomEvents on the <model-viewer> element. Cycle-3 audit DEEP-002 caught
  // that document.addEventListener (bubble-phase) misses non-bubbling events.
  //
  // Fix: use capture-phase listeners. Per DOM spec, the capture phase
  // traverses ancestors from document down to the target BEFORE the at-target
  // dispatch, regardless of whether the event bubbles. Capture-phase document
  // listeners do see non-bubbling element-targeted events.
  //
  // ─── AR entry + outcome ─────────────────────────────────────────────────────
  // Until 2026-07-29 the ONLY AR signal was `interaction_type: 'ar'` on
  // ar-status 'session-started'. That records sessions that BEGAN and is silent
  // on every other outcome, so a visitor who tapped "Preview actual size on
  // your wall" and then dismissed iOS Quick Look's permission dialog is
  // indistinguishable in the event stream from one who never tapped at all.
  // Order WO-4881 was exactly that case: the tap existed only in the rrweb
  // replay, recoverable by watching it back by hand and not aggregatable.
  //
  // Two additions, neither overloading `interaction_type: 'ar'` — that value
  // keeps its historical meaning (a session actually started) so the existing
  // series stays comparable across the change:
  //   ar_requested — a tap on an AR entry control.
  //   ar_status    — ar-status values other than session-started, each one
  //                  reported at most once per AR episode.
  // Together: requested-but-no-session = dismissed or broken; requested +
  // 'failed' = broken; requested + session-started = it worked.
  //
  // Device split needs no property here — the collector already stamps
  // device_type, and on desktop the same button shows a QR code rather than
  // launching AR, so filter to mobile when reading these as an AR funnel.
  var AR_ENTRY_SELECTOR =
    '#PCWallPreviewBtn, .product-single__view-in-space, [slot="ar-button"]';

  /* PURE HELPERS BEGIN — extracted + unit-tested in tests/ar-status-fanout.test.mjs */
  //
  // One human AR attempt is an EPISODE. It opens on a tap of an AR entry
  // control (or, for the ?ar=1 auto-launch handoff in custom-product-ui.js,
  // on a bare 'session-started' — that path calls activateAR() directly, so
  // there is no tap to observe) and closes with exactly ONE terminal
  // ar_status, attributed to the variant the episode was opened for. A
  // mid-session progress status ('object-placed') may report once alongside
  // it; it never stands in for the exit row.
  //
  // Why a decision and not a different binding: model-viewer 4.1.0 drives AR
  // from a single shared arRenderer and deliberately short-circuits its
  // presented-scene check for the value 'not-presenting', so it re-dispatches
  // that one status on EVERY connected <model-viewer>. The affected PDP
  // renders 18 of them (14 per-variant + 4 Format Explainer), so on
  // 2026-08-09 each AR exit wrote 18 rows, each attributed to whichever
  // element the library happened to reach. A per-element binding fans out
  // identically; only de-duplication + episode-scoped attribution fixes it.
  //
  // The wall-preview button forwards its click to model-viewer's own slotted
  // AR button, so ONE human tap produces up to two matching clicks ~100ms
  // apart (measured on the WO-4881 replay). First wins; the rest fall inside
  // the guard. A genuine re-tap after a failure is far slower than this.
  var AR_DEDUPE_MS = 1000;
  // How long after the episode has ALREADY ENDED a further status still counts
  // as part of that exit. Measured burst spans on 2026-08-09 were 0.71s /
  // 1.27s / 0.94s across 18 elements; 5000ms is ~4x the widest observed. The
  // length is not load-bearing for the fan-out itself — a given status is
  // reported at most once per episode however long the burst runs — it only
  // bounds how long after a terminal status a further one is still this exit.
  var AR_TERMINAL_DEDUPE_MS = 5000;

  // model-viewer 4.1.0's ARStatus is EXACTLY these four values, enumerated from
  // the pinned bundle we load (ajax.googleapis.com/ajax/libs/model-viewer/
  // 4.1.0/model-viewer.min.js — `Ug="not-presenting", Pg="session-started",
  // Fg="object-placed", Ng="failed"`; 'tracking'/'not-tracking' belong to the
  // separate ar-tracking event, and there is no 'unsupported' status):
  //
  //   session-started  PROGRESS — the XR session opened.
  //   object-placed    PROGRESS — the visitor landed the artwork on their wall
  //                    mid-session (dispatched from the initial hit-test).
  //   not-presenting   TERMINAL — the session tore down. Broadcast to EVERY
  //                    connected element, which is the fan-out this fixes.
  //   failed           TERMINAL — AR could not be entered / became unavailable.
  //
  // The split is load-bearing, not decorative: a PROGRESS status must never
  // occupy the episode's last-status slot as if it ended the episode, or the
  // staleness guard below eats the real exit row for every visitor who lingers
  // more than AR_TERMINAL_DEDUPE_MS on a placed artwork — i.e. exactly the
  // successful sessions these events exist to capture (live session
  // e0f9d680: object-placed 13:16:00.182 -> exit 13:16:21.858, 21.7s apart).
  // An UNKNOWN status (a future library version) is treated as PROGRESS on
  // purpose: mis-classifying a terminal status as progress costs one extra
  // row, mis-classifying a progress status as terminal costs the exit row.
  var AR_TERMINAL_STATUSES = { 'not-presenting': true, failed: true };

  function arIsTerminalStatus(status) {
    return AR_TERMINAL_STATUSES[status] === true;
  }

  function arInitialState() {
    return {
      arRequestedAt: 0,
      requestedVariantId: null,
      arSessionSeen: false,
      lastTerminalAt: 0,
      lastTerminalStatus: null
    };
  }

  function arReadState(input) {
    return {
      arRequestedAt: input.arRequestedAt || 0,
      requestedVariantId: input.requestedVariantId || null,
      arSessionSeen: input.arSessionSeen === true,
      lastTerminalAt: input.lastTerminalAt || 0,
      lastTerminalStatus: input.lastTerminalStatus || null
    };
  }

  // A tap on an AR entry control. Every state field is RESET rather than
  // merged: episode 2's after_session must not inherit episode 1's session,
  // and its terminal event must not be swallowed as a duplicate of episode 1's.
  //
  // NOTE FOR ANALYSIS: this changes what after_session MEANS across this
  // commit. Before it, after_session was a page-load latch — once any session
  // started, every later ar_status on that page-load read true. From here it
  // is per episode. Rows written before the fix landed are therefore not
  // comparable with later ones for multi-episode page-loads — the same
  // caveat that already covers the polluted 2026-08-09 fan-out rows.
  function arRequestDecision(input) {
    var state = arReadState(input);
    var now = input.now;
    if (state.arRequestedAt && now - state.arRequestedAt < AR_DEDUPE_MS) {
      return { emit: false, variantId: null, nextState: state };
    }
    return {
      emit: true,
      variantId: input.variantId || null,
      nextState: {
        arRequestedAt: now,
        requestedVariantId: input.variantId || null,
        arSessionSeen: false,
        lastTerminalAt: 0,
        lastTerminalStatus: null
      }
    };
  }

  // An ar-status event. Returns which event (if any) to send, what to attribute
  // it to, and the episode state to carry forward.
  function arStatusDecision(input) {
    var state = arReadState(input);
    var status = input.status;
    var now = input.now;
    var none = {
      emit: false,
      event: null,
      interactionType: null,
      variantId: null,
      afterSession: state.arSessionSeen,
      nextState: state
    };
    if (!status) return none;

    if (status === 'session-started') {
      // The library gates 'session-started' to the instance that is actually
      // presenting, so this one never fans out. Attribute it to that element,
      // falling back to the requesting variant. It also OPENS an episode, for
      // AR entered outside AR_ENTRY_SELECTOR.
      var started = input.targetVariantId || state.requestedVariantId || null;
      return {
        emit: true,
        event: 'model_viewer_interacted',
        interactionType: 'ar',
        variantId: started,
        afterSession: true,
        nextState: {
          arRequestedAt: state.arRequestedAt || now,
          requestedVariantId: state.requestedVariantId || started,
          arSessionSeen: true,
          lastTerminalAt: 0,
          lastTerminalStatus: null
        }
      };
    }

    // 'not-presenting' is also model-viewer's idle/default state and is
    // dispatched with no user action at all, so only report an outcome once AR
    // was actually requested or started in this page-load. Ungated this would
    // repeat the 16,410-bogus-rotate mistake (see the camera-change handler).
    if (!state.arRequestedAt && !state.arSessionSeen) return none;

    // lastTerminalStatus holds the last status REPORTED in this episode —
    // progress or terminal. (The field name predates the progress/terminal
    // split and is load-bearing in the tests, so it stays.)
    if (state.lastTerminalStatus !== null) {
      // The same status again is the broadcast fan-out — drop it, however long
      // the burst runs. So each ARStatus is reported at most once per episode,
      // which is also what bounds an unknown future status.
      if (state.lastTerminalStatus === status) return none;
      // A genuinely DIFFERENT status carries real information and is reported:
      // 'failed' then 'not-presenting', or 'object-placed' then the exit.
      // The staleness guard applies only once a TERMINAL status has already
      // ended the episode — after that, a later status is a page-level idle
      // transition, not this exit. A progress status must never start that
      // clock: it is mid-session, and the exit is still to come.
      var endedAlready = arIsTerminalStatus(state.lastTerminalStatus);
      var stillThisExit = now - state.lastTerminalAt < AR_TERMINAL_DEDUPE_MS;
      if (endedAlready && !stillThisExit) return none;
    }

    return {
      emit: true,
      event: 'ar_status',
      interactionType: null,
      // The variant the episode was opened for — NOT re-derived from the
      // element the library broadcast to, which is the whole defect.
      variantId: state.requestedVariantId || input.targetVariantId || null,
      afterSession: state.arSessionSeen,
      nextState: {
        arRequestedAt: state.arRequestedAt,
        requestedVariantId: state.requestedVariantId,
        arSessionSeen: state.arSessionSeen,
        lastTerminalAt: now,
        lastTerminalStatus: status
      }
    };
  }
  /* PURE HELPERS END */

  var _arState = arInitialState();
  var _lastVariantId = null;

  // product.variant_id is server-rendered once (selected_or_first_available_
  // variant) and never updated, so it is the FIRST variant, not the selected
  // one. AR is per-variant — each size has its own model — so resolve the live
  // one: the enclosing model-viewer's id (`model-viewer-<variantId>`) is
  // authoritative when the event landed inside one, then the last
  // variant:change, then the URL, then the server-rendered fallback.
  function currentVariantId(el) {
    var mv = el && el.closest ? el.closest('model-viewer') : null;
    if (mv && mv.id && mv.id.indexOf('model-viewer-') === 0) {
      return mv.id.slice('model-viewer-'.length);
    }
    if (_lastVariantId) return String(_lastVariantId);
    var m = /[?&]variant=(\d+)/.exec(window.location.search);
    if (m) return m[1];
    var product = window.PeterCox.product;
    return product && product.variant_id ? String(product.variant_id) : null;
  }

  function startArEntryListener() {
    document.addEventListener('click', function (evt) {
      var target = evt.target;
      if (!target || !target.closest) return;
      var btn = target.closest(AR_ENTRY_SELECTOR);
      if (!btn) return;
      var decision = arRequestDecision({
        now: Date.now(),
        variantId: currentVariantId(btn),
        arRequestedAt: _arState.arRequestedAt,
        requestedVariantId: _arState.requestedVariantId,
        arSessionSeen: _arState.arSessionSeen,
        lastTerminalAt: _arState.lastTerminalAt,
        lastTerminalStatus: _arState.lastTerminalStatus
      });
      _arState = decision.nextState;
      if (!decision.emit) return;
      var product = window.PeterCox.product;
      track('ar_requested', {
        product_id: product ? product.id : null,
        variant_id: decision.variantId,
        control: btn.id === 'PCWallPreviewBtn' ? 'wall_preview' : 'model_viewer'
      });
    }, true /* capture */);
  }

  var _modelDebounce = null;
  function startModelViewerListener() {
    document.addEventListener('camera-change', function (evt) {
      // model-viewer fires camera-change for programmatic moves too — the
      // initial framing and reveal animation carry detail.source 'automatic'
      // or 'none'; only a genuine user orbit/zoom carries 'user-interaction'.
      // Without this gate every page load (crawlers included) logged a bogus
      // "rotate" — 16,410 in 4 days. See task mwXsG3tuHf88.
      if (!evt || !evt.detail || evt.detail.source !== 'user-interaction') return;
      if (_modelDebounce) return;
      _modelDebounce = setTimeout(function () {
        _modelDebounce = null;
        var product = window.PeterCox.product;
        track('model_viewer_interacted', {
          product_id: product ? product.id : null,
          interaction_type: 'rotate'
        });
      }, 1500);
    }, true /* capture */);
    document.addEventListener('ar-status', function (evt) {
      var status = evt && evt.detail && evt.detail.status;
      if (!status) return;
      // Thin adapter: gather the runtime facts, let arStatusDecision decide.
      // model-viewer broadcasts 'not-presenting' to every connected element,
      // so this handler runs 18x per AR exit on a PDP and must emit once.
      var decision = arStatusDecision({
        status: status,
        targetVariantId: currentVariantId(evt.target),
        now: Date.now(),
        arRequestedAt: _arState.arRequestedAt,
        requestedVariantId: _arState.requestedVariantId,
        arSessionSeen: _arState.arSessionSeen,
        lastTerminalAt: _arState.lastTerminalAt,
        lastTerminalStatus: _arState.lastTerminalStatus
      });
      _arState = decision.nextState;
      if (!decision.emit) return;
      var product = window.PeterCox.product;
      if (decision.event === 'model_viewer_interacted') {
        // variant_id is additive — interaction_type stays 'ar' so the existing
        // series is unbroken; the new property just says which size it was.
        track('model_viewer_interacted', {
          product_id: product ? product.id : null,
          interaction_type: decision.interactionType,
          variant_id: decision.variantId
        });
        return;
      }
      track('ar_status', {
        product_id: product ? product.id : null,
        variant_id: decision.variantId,
        status: status,
        after_session: decision.afterSession
      });
    }, true /* capture */);
  }

  // ─── Klaviyo newsletter signup ──────────────────────────────────────────────
  // Klaviyo's onsite forms dispatch a global klaviyoForms event with
  // detail.type 'submit'. Fires for newsletter signups, popups, etc.
  //
  // DORMANT, NOT DEAD — do not delete. The site's visible newsletter forms are
  // the theme's own three (footer / inline / popup snippets), which emit
  // newsletter_signup themselves. But the Klaviyo onsite embed IS loaded on the
  // live site (config/settings_data.json app embed, "disabled": false) and
  // Klaviyo holds live forms, so a hosted form can start rendering from the
  // Klaviyo dashboard with NO theme deploy. Dropping this listener would open a
  // silent gap on that day.
  //
  // `surface` is what keeps the two sources separable: if a Klaviyo-hosted form
  // ever renders alongside a custom form, the double-count is VISIBLE in the
  // data instead of silent. Every newsletter_signup emitter in the theme stamps
  // one — footer / inline / popup / klaviyo_widget.
  //
  // SEMANTICS — like the three snippet emitters, this counts an accepted
  // submission, not a net-new subscriber; Klaviyo does not tell us which.
  function startNewsletterListener() {
    window.addEventListener('klaviyoForms', function (evt) {
      var detail = evt && evt.detail;
      if (!detail || detail.type !== 'submit') return;
      track('newsletter_signup', {
        surface: 'klaviyo_widget',
        outcome: 'accepted',
        form_id: detail.formId || null,
        klaviyo_form_id: detail.formId || null
      });
    });
  }

  // ─── Account events (login + signup) ────────────────────────────────────────
  // Shopify customer login + register forms POST to /account/login and /account
  // (register). On a successful submit, Shopify 302s to /account. We can't
  // directly observe success vs failure from the client, so the cheap signal
  // is: at submit time, set a sessionStorage flag with the intent, and on the
  // NEXT page load (which is the destination after the redirect) fire the
  // event. False-positive: a failed login still sets the flag; the user will
  // fire account_login on the very next page they visit. Acceptable because
  // failed logins are rare and re-render the login page, where the next
  // genuine navigation is usually a successful retry.
  function startAccountFormHooks() {
    document.addEventListener('submit', function (evt) {
      var form = evt.target;
      if (!form || !form.action) return;
      var action = String(form.action);
      try {
        if (action.indexOf('/account/login') >= 0) {
          sessionStorage.setItem('pcx_post_account_event', 'login');
        } else if (
          /\/account(\/?|$|\?)/.test(action) &&
          form.querySelector('input[name="customer[password]"]')
        ) {
          // /account POST with a password field == registration form.
          sessionStorage.setItem('pcx_post_account_event', 'signup');
        }
      } catch (e) { /* sessionStorage may throw in privacy modes; skip */ }
    });
  }

  function fireAccountEventIfFlagged() {
    var ev = null;
    try { ev = sessionStorage.getItem('pcx_post_account_event'); }
    catch (e) { return; }
    if (!ev) return;
    try { sessionStorage.removeItem('pcx_post_account_event'); }
    catch (e) { /* ignore */ }
    if (ev !== 'login' && ev !== 'signup') return;
    track('account_' + ev, {});
  }

  // ─── Init ───────────────────────────────────────────────────────────────────
  //
  // The four entry events are now OWNED by the inline entry block in
  // snippets/custom-event-tracker.liquid, which issues them during head parse
  // and sets window.PeterCox.entryFiredTypes before it does. These fires are
  // GUARDED, not deleted: with the entry flags absent, init() fires all four
  // again exactly as it did before 2026-08-16 (test 8a).
  //
  // ROLLBACK CONSTRAINT — THIS FILE AND THE SNIPPET MUST REVERT TOGETHER.
  // Since 2026-08-16 the two files are hard-coupled, and Shopify's theme code
  // editor exposes per-file "Older versions" restore, so each half-revert below
  // is a single click away. Neither is safe:
  //
  //   * Revert ONLY snippets/custom-event-tracker.liquid → window.PeterCox
  //     .buildEnvelope no longer exists, so emit() (:122) throws on EVERY event
  //     and track() (:140) swallows the throw into a console.warn. ALL cairn
  //     theme telemetry stops — not just the entry events: page_viewed,
  //     add_to_cart, checkout_started, web_vitals, form_field. Loud in the
  //     browser console, completely silent in the data.
  //
  //   * Revert ONLY this file → the pre-change unguarded init() fires all four
  //     entry events on top of the ones the snippet already sent during head
  //     parse. EVERY entry event double-counts, and there is no console error
  //     at all. That 2x is the exact signature the spec's AC7 hard-fails on,
  //     and it is typically only noticed when the 14-day measurement is read.
  //
  // To roll back, revert BOTH files to the same pre-2026-08-16 revision.
  //
  // The guard reads the sub-flag per event type — all four, not just
  // page_viewed; a missed sub-flag double-fires and silently inflates every
  // traffic figure (same failure family as the ar_status fan-out,
  // regression 406).
  function entryAlreadyFired(eventType) {
    var P = window.PeterCox;
    if (!P || !P.entryFired) return false;
    return !!(P.entryFiredTypes && P.entryFiredTypes[eventType]);
  }

  function init() {
    if (!entryAlreadyFired('page_viewed')) firePageViewed();
    if (!entryAlreadyFired('product_viewed')) fireProductViewed();
    if (!entryAlreadyFired('collection_viewed')) fireCollectionViewed();
    if (!entryAlreadyFired('search_performed')) fireSearchPerformedIfSearchPage();
    fireAccountEventIfFlagged();
    fireCartAttributesInit();

    document.addEventListener('ajaxProduct:added', onAjaxProductAdded);
    document.addEventListener('product-form:submit:success', onProductFormAdded);
    // Capture phase: must fire BEFORE Shopify's payment-button handler
    // navigates the page, so the POST survives via keepalive.
    document.addEventListener('click', onPaymentButtonClick, true);
    // Capture phase — must run before the cart form's submit navigates away.
    document.addEventListener('click', onCheckoutClick, true);
    document.addEventListener('variant:change', onVariantChange);
    document.addEventListener('cart:updated', onCartUpdated);

    startEngagementTimer();
    startScrollDepthListener();
    startImageZoomListener();
    startGalleryListener();
    startModelViewerListener();
    startArEntryListener();
    startNewsletterListener();
    startAccountFormHooks();
  }

  window.PeterCox.tracker = {
    track: track,
    _emit: emit,
    _ensureVisitorId: ensureVisitorId,
    _ensureSessionId: ensureSessionId
    // _parseAttribution / _getKlaviyoIdCookie were exported here until
    // 2026-08-16. Both functions moved into window.PeterCox.buildEnvelope (the
    // inline entry block); neither export had a consumer anywhere in the theme
    // or the checkout pixel, so they are gone rather than re-pointed.
  };

  // Top-level alias of the SAME full-envelope track() so callers that read
  // window.PeterCox.track (the capture adapter, pcx-capture-adapter.js:50) get
  // the visitor_id/session_id-bearing envelope rather than falling through to a
  // minimal {event_type, properties} body the collector 422s on. Single source
  // of truth — identical reference to window.PeterCox.tracker.track, not a
  // second divergent track.
  window.PeterCox.track = track;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
